// src/services/pumpfun.service.ts
import {
  Connection,
  Transaction,
  LAMPORTS_PER_SOL,
  Keypair,
  VersionedTransaction,
} from '@solana/web3.js';
import { SOLANA_RPC_ENDPOINT } from '../utils/constants';
import { TradingService } from './frontend-bundler/trading.service';
import { apiEndpoints } from './api.service';
import bs58 from 'bs58';

interface PumpfunTokenData {
  name: string;
  symbol: string;
  description: string;
  image: string;
  website?: string;
  twitter?: string;
  telegram?: string;
  discord?: string;
  showName?: boolean;
  createBuyTransaction?: boolean;
  buyAmount?: number;
  slippage?: number;
  priorityFee?: number;
  unitLimit?: number;
  unitPrice?: number;
}

interface PumpfunResponse {
  success: boolean;
  signature?: string; // base64 serialized tx (unsigned or partially signed) OR a final sig (base58)
  mint?: string;
  metadataUri?: string;
  error?: string;
}

interface BundleResponse {
  success: boolean;
  bundleId?: string;
  signatures?: string[];
  error?: string;
}

const isLikelyBase58 = (s: string) => /^[1-9A-HJ-NP-Za-km-z]+$/.test(s);
const isLikelyBase64 = (s: string) => /^[A-Za-z0-9+/=]+$/.test(s);

class PumpfunService {
  private baseUrl: string = 'https://pumpportal.fun/api';
  private connection: Connection;
  private tradingService: TradingService;

  // used to co-sign the local "create" transaction (required by PumpPortal local API)
  private lastMintKeypair?: Keypair;

  constructor() {
    this.connection = new Connection(SOLANA_RPC_ENDPOINT, 'confirmed');
    this.tradingService = new TradingService(this.connection);
  }

  async createToken(
  data: PumpfunTokenData,
  walletAddress: string
): Promise<PumpfunResponse> {
  try {
    console.log('🚀 Creating Pump.fun token (local)...');

    // 1) Generate mint for local create
    this.lastMintKeypair = Keypair.generate();

    // 2) Upload metadata to IPFS FIRST
    console.log('Uploading metadata to IPFS...');
    
    // Create FormData for the IPFS upload
    const formData = new FormData();
    
    // If image is a URL, we need to fetch it first
    if (data.image.startsWith('http')) {
      const imageResponse = await fetch(data.image);
      const blob = await imageResponse.blob();
      formData.append('file', blob, 'image.png');
    } else {
      // If it's base64, convert to blob
      const base64Data = data.image.split(',')[1];
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'image/png' });
      formData.append('file', blob, 'image.png');
    }
    
    formData.append('name', data.name);
    formData.append('symbol', data.symbol);
    formData.append('description', data.description);
    formData.append('twitter', data.twitter || '');
    formData.append('telegram', data.telegram || '');
    formData.append('website', data.website || '');
    formData.append('discord', data.discord || '');
    formData.append('showName', 'true');

    // Upload to Pump.fun's IPFS
    const metadataResponse = await fetch('https://pump.fun/api/ipfs', {
      method: 'POST',
      body: formData
    });

    if (!metadataResponse.ok) {
      throw new Error('Failed to upload metadata to IPFS');
    }

    const metadataJson = await metadataResponse.json();
    const metadataUri = metadataJson.metadataUri;
    
    console.log('Metadata URI:', metadataUri);

    // 3) Create token with the URI
    const payload = {
      publicKey: walletAddress,
      action: 'create',
      tokenMetadata: {
        name: data.name,
        symbol: data.symbol,
        uri: metadataUri // Use the URI from IPFS upload
      },
      mint: this.lastMintKeypair.publicKey.toBase58(),
      denominatedInSol: 'true',
      amount: data.buyAmount || 0,
      slippage: data.slippage ?? 10,
      priorityFee: data.priorityFee ?? 0.0005,
      pool: 'pump'
    };

    console.log('Creating token with payload:', payload);

    // 4) Get the transaction
    const resp = await fetch(`${this.baseUrl}/trade-local`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });

    if (!resp.ok) {
      const errText = await resp.text();
      throw new Error(`Failed to get transaction: ${errText}`);
    }

    // 5) Process response
    const contentType = resp.headers.get('content-type') || '';
    let base64Tx: string;
    
    if (contentType.includes('application/octet-stream') || contentType.includes('application/json')) {
      const buf = await resp.arrayBuffer();
      base64Tx = Buffer.from(buf).toString('base64');
    } else {
      const text = await resp.text();
      base64Tx = isLikelyBase64(text) ? text : Buffer.from(text).toString('base64');
    }

    return {
      success: true,
      signature: base64Tx,
      mint: this.lastMintKeypair.publicKey.toBase58(),
      metadataUri
    };

  } catch (error: any) {
    console.error('❌ Failed to create Pump.fun token:', error);
    return { success: false, error: error.message };
  }
}
  /**
   * Extract mint address from various response formats
   */
  extractMintAddress(result: any): string | undefined {
    const possibleFields = [
      'mint',
      'tokenAddress',
      'token',
      'contractAddress',
      'mintAddress',
      'tokenMint',
    ];

    for (const field of possibleFields) {
      if (result?.[field]) return result[field];
    }

    if (result?.data) {
      for (const field of possibleFields) {
        if (result.data?.[field]) return result.data[field];
      }
    }

    console.log('Could not extract mint address from result');
    return undefined;
  }

  /**
   * Enhanced mint extraction from transaction with retry logic
   */
  async getMintFromSignature(
    signature: string,
    walletAddress?: string
  ): Promise<string | undefined> {
    try {
      if (!isLikelyBase58(signature)) {
        console.warn('getMintFromSignature: not a base58 signature, skipping.');
        return undefined;
      }

      console.log('Waiting for transaction confirmation...');
      await new Promise((r) => setTimeout(r, 5000));

      let tx: Awaited<ReturnType<Connection['getParsedTransaction']>> | null =
        null;
      let retries = 3;

      while (!tx && retries > 0) {
        tx = await this.connection.getParsedTransaction(signature, {
          maxSupportedTransactionVersion: 0,
          commitment: 'confirmed',
        });

        if (!tx) {
          retries--;
          console.log(
            `Transaction not found, retrying... (${retries} attempts left)`
          );
          await new Promise((r) => setTimeout(r, 2000));
        }
      }

      if (!tx) {
        console.error('Transaction not found after retries');
        return undefined;
      }

      // Method 1: check postTokenBalances
      if (tx.meta?.postTokenBalances?.length) {
        for (const balance of tx.meta.postTokenBalances) {
          const amount = balance.uiTokenAmount?.uiAmount;
          if (
            walletAddress &&
            balance.owner === walletAddress &&
            typeof amount === 'number' &&
            amount > 0
          ) {
            console.log('Found mint from token balance:', balance.mint);
            return balance.mint;
          }
        }

        for (const balance of tx.meta.postTokenBalances) {
          const amount = balance.uiTokenAmount?.uiAmount;
          if (typeof amount === 'number' && amount >= 1_000_000_000) {
            console.log('Found mint from large supply:', balance.mint);
            return balance.mint;
          }
        }
      }

      // Method 2: check logs
      if (tx.meta?.logMessages?.length) {
        for (const log of tx.meta.logMessages) {
          const mintMatch = log.match(/mint[:\s]+([A-HJ-NP-Za-km-z1-9]{32,44})/i);
          if (mintMatch) {
            console.log('Found mint from logs:', mintMatch[1]);
            return mintMatch[1];
          }
          const tokenMatch = log.match(/token[:\s]+([A-HJ-NP-Za-km-z1-9]{32,44})/i);
          if (tokenMatch) {
            console.log('Found token from logs:', tokenMatch[1]);
            return tokenMatch[1];
          }
        }
      }

      // Method 3: check outer instructions
      const instructions = tx.transaction.message.instructions ?? [];
      for (const instruction of instructions as any[]) {
        if ('parsed' in instruction && instruction.parsed) {
          const parsed = instruction.parsed as {
            type?: string;
            info?: { mint?: string; newAccount?: string };
          };

          if (parsed?.type === 'initializeMint' || parsed?.type === 'initializeMint2') {
            const mint = parsed.info?.mint;
            if (mint) {
              console.log('Found mint from instruction:', mint);
              return mint;
            }
          }

          if (parsed?.type === 'createAccount') {
            const newAccount = parsed.info?.newAccount;
            if (newAccount) {
              console.log('Found account from instruction:', newAccount);
              return newAccount;
            }
          }
        }
      }

      // Method 4: inner instructions
      if (tx.meta?.innerInstructions?.length) {
        for (const inner of tx.meta.innerInstructions as any[]) {
          for (const instruction of (inner.instructions ?? []) as any[]) {
            if ('parsed' in instruction && instruction.parsed) {
              const parsed = instruction.parsed as {
                type?: string;
                info?: { mint?: string; newAccount?: string };
              };
              if (
                parsed?.type === 'initializeMint' ||
                parsed?.type === 'createAccount'
              ) {
                const mint = parsed.info?.mint || parsed.info?.newAccount;
                if (mint) {
                  console.log('Found mint from inner instruction:', mint);
                  return mint;
                }
              }
            }
          }
        }
      }

      console.log('Could not extract mint from transaction');
      return undefined;
    } catch (error) {
      console.error('Failed to get mint from signature:', error);
      return undefined;
    }
  }

  /**
   * Buy token using a specific keypair (for bundle wallets)
   */
  async buyTokenWithKeypair(
    tokenMint: string,
    keypair: Keypair,
    amountInSol: number,
    slippage: number = 10
  ): Promise<{ success: boolean; signature?: string; error?: string }> {
    try {
      const result = await this.tradingService.swapTokens(
        keypair,
        tokenMint,
        amountInSol,
        slippage * 100 // percent -> bps
      );

      return {
        success: result.success,
        signature: result.signature,
        error: result.error,
      };
    } catch (error: any) {
      console.error(`Buy failed for ${keypair.publicKey.toBase58()}:`, error);
      return {
        success: false,
        error: error.message,
      };
    }
  }

  /**
   * (Stub) Create token with a bundle flow.
   * NOTE: PumpPortal does NOT expose /trade/bundle. For bundled submits,
   * you request an ARRAY of local args, sign each tx (create needs mint + wallet),
   * then submit to Jito block-engine yourself.
   */
  async createTokenWithBundle(
    data: PumpfunTokenData,
    walletAddress: string,
    jitoTip: number = 0.01
  ): Promise<BundleResponse> {
    try {
      console.log('🎯 Creating bundled Pump.fun token (local + wallet + Jito)...');

      const mintKeypair = Keypair.generate();

      const tokenMetadata: any = {
        name: data.name,
        symbol: data.symbol,
        description: data.description,
        file: data.image,
        twitter: data.twitter,
        telegram: data.telegram,
        website: data.website,
        discord: data.discord,
        showName: data.showName ?? true,
        creatorBuyAmount: data.buyAmount || 0,
      };

      // Build array of local args. First tx is create; you can add more (buys) after.
      const bundledArgs: any[] = [
        {
          publicKey: walletAddress,
          action: 'create',
          tokenMetadata,
          mint: mintKeypair.publicKey.toBase58(),
          denominatedInSol: 'true',
          amount: data.buyAmount || 0,
          slippage: data.slippage ?? 10,
          priorityFee: data.priorityFee ?? 0.0001,
          pool: 'pump',
          useVersionedTransaction: true,
        },
        // Optionally push additional buys here
        // { publicKey: walletAddress, action: 'buy', mint: mintKeypair.publicKey.toBase58(), ... }
      ];

      // Request an array of serialized transactions (base58 strings)
      const txResp = await fetch(`${this.baseUrl}/trade-local`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bundledArgs),
      });

      if (!txResp.ok) {
        const err = await txResp.text();
        throw new Error(`Failed to get bundled transactions: ${err}`);
      }

      // The local API may return base58-encoded serialized v0 txs in a JSON array.
      const encodedTxsBase58: string[] = await txResp.json();

      // Partially sign the create tx with the mint keypair.
      // Wallet must co-sign later in UI before sending to Jito.
      const reencodedAfterMintSign: string[] = [];
      for (let i = 0; i < encodedTxsBase58.length; i++) {
        const raw = bs58.decode(encodedTxsBase58[i]); // Uint8Array
        const vtx = VersionedTransaction.deserialize(raw);

        if (bundledArgs[i].action === 'create') {
          vtx.sign([mintKeypair]);
        }

        reencodedAfterMintSign.push(bs58.encode(vtx.serialize()));
      }

      // TODO: ask wallet to sign all, submit to Jito with tip.
      return {
        success: true,
        bundleId: undefined,
        signatures: [],
      };
    } catch (error: any) {
      console.error('❌ Bundle creation failed:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Upload image - keeping your existing Cloudinary implementation
   */
  async uploadToIPFS(file: File): Promise<string> {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(
        `${apiEndpoints.uploadLogo}?folder=pumpfun-tokens&prefix=pump`,
        {
          method: 'POST',
          body: formData,
        }
      );

      if (response.ok) {
        const data = await response.json();
        const imageUrl = data.secure_url || data.url || data.imageUrl;
        if (imageUrl) {
          console.log('✅ Uploaded to Cloudinary:', imageUrl);
          return imageUrl;
        }
      }

      console.log('⚠️ Upload failed, using base64 fallback');
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    } catch (error) {
      console.error('Upload failed:', error);
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    }
  }

  /**
   * Buy tokens on Pump.fun (local)
   */
  async buyToken(
    mintAddress: string,
    amount: number,
    walletAddress: string,
    slippage: number = 10,
    priorityFee: number = 0.0005
  ): Promise<PumpfunResponse> {
    try {
      const payload = {
        publicKey: walletAddress,
        action: 'buy',
        mint: mintAddress,
        denominatedInSol: 'true',
        amount,
        slippage,
        priorityFee,
        pool: 'pump',
        unitLimit: 250000,
        unitPrice: 66000,
      };

      const response = await fetch(`${this.baseUrl}/trade-local`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const err = await response.text();
        throw new Error(`Failed to get buy transaction: ${err}`);
      }

      const buf = await response.arrayBuffer();
      const base64Tx = Buffer.from(buf).toString('base64');

      return {
        success: true,
        signature: base64Tx, // serialized v0 tx for wallet to sign & send
      };
    } catch (error: any) {
      console.error('❌ Buy transaction failed:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Sell tokens on Pump.fun (local)
   */
  async sellToken(
    mintAddress: string,
    amount: number,
    walletAddress: string,
    sellAll: boolean = false,
    slippage: number = 10,
    priorityFee: number = 0.0005
  ): Promise<PumpfunResponse> {
    try {
      const payload = {
        publicKey: walletAddress,
        action: 'sell',
        mint: mintAddress,
        denominatedInSol: sellAll ? 'false' : 'true',
        amount: sellAll ? 100 : amount,
        slippage,
        priorityFee,
        pool: 'pump',
        unitLimit: 250000,
        unitPrice: 66000,
      };

      const response = await fetch(`${this.baseUrl}/trade-local`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const err = await response.text();
        throw new Error(`Failed to get sell transaction: ${err}`);
      }

      const buf = await response.arrayBuffer();
      const base64Tx = Buffer.from(buf).toString('base64');

      return {
        success: true,
        signature: base64Tx, // serialized v0 tx for wallet to sign & send
      };
    } catch (error: any) {
      console.error('❌ Sell transaction failed:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Sign (with local mint keypair if present) + wallet, then send.
   * Accepts both Versioned (v0) and legacy transactions.
   */
  async signAndSendTransaction(
    transactionEncoded: string,
    signTransaction: (
      tx: Transaction | VersionedTransaction
    ) => Promise<Transaction | VersionedTransaction>
  ): Promise<string> {
    try {
      // If it's a base58-looking string (signature), return as-is
      if (isLikelyBase58(transactionEncoded) && !isLikelyBase64(transactionEncoded)) {
        console.log('Looks like a finalized signature, returning as-is');
        return transactionEncoded;
      }

      // Otherwise, assume it's a base64-encoded serialized transaction
      if (!isLikelyBase64(transactionEncoded)) {
        throw new Error('Unexpected transaction format (not base64 or base58).');
      }

      const bytes = Buffer.from(transactionEncoded, 'base64');
      let tx: Transaction | VersionedTransaction | null = null;

      // Try Versioned first
      try {
        tx = VersionedTransaction.deserialize(new Uint8Array(bytes));
      } catch {
        // Fallback to legacy
        tx = Transaction.from(bytes);
      }

      // Partial sign with mint (only if versioned)
      if (tx instanceof VersionedTransaction && this.lastMintKeypair) {
        tx.sign([this.lastMintKeypair]);
      }

      // Wallet co-sign
      const signed = await signTransaction(tx);

      // Send
      const wire =
        signed instanceof VersionedTransaction
          ? signed.serialize()
          : signed.serialize({ requireAllSignatures: false });

      const sig = await this.connection.sendRawTransaction(wire, {
        skipPreflight: false,
        preflightCommitment: 'processed',
      });

      // Confirm
      const latest = await this.connection.getLatestBlockhash();
      await this.connection.confirmTransaction(
        {
          signature: sig,
          blockhash: latest.blockhash,
          lastValidBlockHeight: latest.lastValidBlockHeight,
        },
        'confirmed'
      );

      return sig;
    } catch (error: any) {
      console.error('❌ Transaction failed:', error);
      throw error;
    } finally {
      // clear after one use
      this.lastMintKeypair = undefined;
    }
  }

  /**
   * Get token metadata from Pump.fun
   */
  async getTokenMetadata(mintAddress: string): Promise<any> {
    try {
      const response = await fetch(`${this.baseUrl}/token/${mintAddress}`);

      if (!response.ok) {
        throw new Error('Failed to fetch token metadata');
      }

      return await response.json();
    } catch (error: any) {
      console.error('❌ Failed to get token metadata:', error);
      return null;
    }
  }

  /**
   * Check if a token is on Pump.fun
   */
  async isTokenOnPump(mintAddress: string): Promise<boolean> {
    try {
      const metadata = await this.getTokenMetadata(mintAddress);
      return metadata && metadata.pool === 'pump';
    } catch {
      return false;
    }
  }

  /**
   * Get Pump.fun fee structure
   */
  getPumpfunFees(): {
    creationFee: number;
    tradingFee: number;
    migrationFee: number;
  } {
    return {
      creationFee: 0.02, // SOL
      tradingFee: 0.01, // 1% trading fee
      migrationFee: 3, // SOL (to migrate to Raydium)
    };
  }
}

export default new PumpfunService();
